import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaEdit, FaTrashAlt, FaQrcode, FaUnlink, FaPause, FaPlay } from "react-icons/fa";
import OperatorAccessModal from "./OperatorAccessModal";
import OperatorTermsModal from "./OperatorTermsModal";
import { formatOperatorTermsAcceptedAt } from "../../legal/printOperatorTerms";
import "./OperatorPlanQuotaBar.css";
import "./OperatorStatus.css";

function isAlertPending(pending) {
  return Boolean(pending && pending.code && !pending.acknowledged);
}

const Card_operator = (props) => {
  const {
    operator,
    showPlanBadges,
    onRequestDelete,
    onRequestUnpair,
    onRequestPause,
    onRequestResume,
    autoOpenAccess,
    onAccessViewed,
    onClausesChanged,
  } = props;
  const [accessOpen, setAccessOpen] = useState(Boolean(autoOpenAccess));
  const [termsOpen, setTermsOpen] = useState(false);
  const ignoreOpenUntil = useRef(0);
  const dismissedRef = useRef(false);
  const pending =
    operator.access_pending && operator.access_pending.code
      ? operator.access_pending
      : null;
  const alertPending = isAlertPending(pending);

  useEffect(() => {
    if (autoOpenAccess && alertPending && !dismissedRef.current) {
      setAccessOpen(true);
    }
  }, [autoOpenAccess, alertPending]);

  function handleDeleteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (onRequestDelete) {
      onRequestDelete(operator);
    }
  }

  function handleUnpairClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (onRequestUnpair) {
      onRequestUnpair(operator);
    }
  }

  function handlePauseClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (operator.app_access_paused) {
      if (onRequestResume) onRequestResume(operator);
      return;
    }
    if (onRequestPause) onRequestPause(operator);
  }

  function openAccess(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (Date.now() < ignoreOpenUntil.current) {
      return;
    }
    if (pending) {
      setAccessOpen(true);
    }
  }

  function openTerms(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setTermsOpen(true);
  }

  function closeTerms(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setTermsOpen(false);
  }

  function closeAccess(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    dismissedRef.current = true;
    ignoreOpenUntil.current = Date.now() + 400;
    if (onAccessViewed && pending && !pending.acknowledged) {
      onAccessViewed(operator);
    }
    window.setTimeout(() => setAccessOpen(false), 50);
  }

  return (
    <div className="col-sm-12  col-md-6 col-lg-6">
      <div className="container">
        <div
          className={
            "x_panel_no_width col-xs-12 z-depth-2" +
            (operator.plan_included ? " operator-card--licensed" : "") +
            (operator.telemetry_online && !alertPending
              ? " operator-card--online"
              : "") +
            (alertPending ? " operator-card--access-pending" : "") +
            (operator.app_access_paused ? " operator-card--paused" : "")
          }
        >
          <a className="pull-left border-aero">
            <img
              src={operator.thumbnail}
              className="img-circle profile_img"
              id="img-circle"
              alt="..."
            />
          </a>
          <div className="media-body">
            <div className="pull-right operator-card-actions">
              {alertPending ? (
                <button
                  type="button"
                  className="btn btn-link operator-access-alert"
                  onClick={openAccess}
                  title={props.t("Pedido de acesso do app")}
                  aria-label={props.t("Pedido de acesso do app")}
                >
                  <FaQrcode />
                </button>
              ) : null}
              <button
                type="button"
                className={
                  "btn btn-link operator-access-toggle" +
                  (operator.app_access_paused
                    ? " operator-access-toggle--resume"
                    : "")
                }
                onClick={handlePauseClick}
                title={
                  operator.app_access_paused
                    ? props.t("Reativar acesso ao app")
                    : props.t("Pausar acesso ao app")
                }
                aria-label={
                  operator.app_access_paused
                    ? props.t("Reativar acesso de {{name}}", {
                        name: operator.nome,
                      })
                    : props.t("Pausar acesso de {{name}}", {
                        name: operator.nome,
                      })
                }
                style={{ padding: 0, border: 0, background: "none" }}
              >
                {operator.app_access_paused ? <FaPlay /> : <FaPause />}
              </button>
              <Link to={operator.id + "/change"}>
                <FaEdit />
              </Link>
              <button
                type="button"
                className="btn btn-link"
                onClick={handleDeleteClick}
                title={props.t("Excluir operador")}
                aria-label={props.t("Excluir operador {{name}}", {
                  name: operator.nome,
                })}
                style={{ padding: 0, border: 0, background: "none" }}
              >
                <FaTrashAlt />
              </button>
              {operator.device_paired ? (
                <button
                  type="button"
                  className="btn btn-link operator-unpair-action"
                  onClick={handleUnpairClick}
                  title={props.t("Desparear do aplicativo")}
                  aria-label={props.t("Desparear {{name}} do aplicativo", {
                    name: operator.nome,
                  })}
                  style={{ padding: 0, border: 0, background: "none" }}
                >
                  <FaUnlink />
                </button>
              ) : null}
            </div>
            <div className="container-fluid col-md-offset-1">
              <h4>
                <span
                  className={
                    operator.telemetry_online
                      ? "operator-status-dot operator-status-dot--online"
                      : "operator-status-dot"
                  }
                  title={
                    operator.telemetry_online
                      ? props.t("Telemetria ligada no app")
                      : props.t("Telemetria desligada")
                  }
                  aria-label={
                    operator.telemetry_online
                      ? props.t("Telemetria ligada no app")
                      : props.t("Telemetria desligada")
                  }
                />
                {pending ? (
                  <button
                    type="button"
                    className={
                      "operator-access-name" +
                      (alertPending ? "" : " operator-access-name--seen")
                    }
                    onClick={openAccess}
                    title={
                      alertPending
                        ? props.t("Clique para liberar o acesso do app")
                        : props.t(
                            "Este operador tentou acessar o app. Clique para ver o código de novo."
                          )
                    }
                  >
                    {operator.nome}
                    {alertPending ? (
                      <span className="operator-access-pill">
                        {props.t("Tentando conectar")}
                      </span>
                    ) : null}
                  </button>
                ) : (
                  operator.nome
                )}
                {operator.app_access_paused ? (
                  <span className="operator-card-plan-badge operator-card-access-badge--paused">
                    {props.t("Acesso pausado")}
                  </span>
                ) : null}
                {showPlanBadges && operator.plan_included ? (
                  <span className="operator-card-plan-badge operator-card-plan-badge--included">
                    {props.t("Licença")}
                  </span>
                ) : null}
                {showPlanBadges && !operator.plan_included ? (
                  <span className="operator-card-plan-badge operator-card-plan-badge--extra">
                    {props.t("Adicional")}
                  </span>
                ) : null}
              </h4>

              {alertPending ? (
                <button
                  type="button"
                  className="operator-access-banner"
                  onClick={openAccess}
                >
                  {props.t(
                    "Este operador está pedindo acesso no app. Clique para ver o código e o QR."
                  )}
                </button>
              ) : null}

              <p>
                {props.t("Identificação")} : <b>{operator.ident}</b>
              </p>
              <p>
                {props.t("Telefone")} : <b>{operator.telefone}</b>
              </p>
              <p>
                {props.t("Aplicativo")} :{" "}
                <b>
                  {operator.app_access_paused
                    ? props.t("Acesso pausado")
                    : operator.device_paired
                      ? props.t("Pareado neste aparelho")
                      : props.t("Não pareado")}
                </b>
              </p>
              {operator.device_paired ? (
                <button
                  type="button"
                  className="operator-unpair-line"
                  onClick={handleUnpairClick}
                >
                  {props.t("Desparear aplicativo")}
                </button>
              ) : null}
              <button
                type="button"
                className="operator-terms-line"
                onClick={openTerms}
                title={props.t("Abrir o texto do termo aceito no app")}
              >
                {operator.app_terms_accepted
                  ? props.t("Leu e aceitou os termos no app em {{when}}", {
                      when:
                        formatOperatorTermsAcceptedAt(
                          operator.app_terms_accepted_at
                        ) || "—",
                    })
                  : props.t("Ainda não aceitou os termos no app")}
              </button>

              <div className="" align="right">
                <small>
                  {props.t("Cadastrado na fazenda")}:{" "}
                  <b>{operator.farm_name}</b>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
      {accessOpen ? (
        <OperatorAccessModal
          pending={pending}
          operatorName={operator.nome}
          onClose={closeAccess}
        />
      ) : null}
      {termsOpen ? (
        <OperatorTermsModal
          operator={operator}
          farmId={props.farm_id}
          onClose={closeTerms}
          onClausesChanged={onClausesChanged}
        />
      ) : null}
    </div>
  );
};

export default withTranslation()(Card_operator);
